import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiFailure } from '../../lib/api';
import { ME_QUERY_KEY } from './useOperatorMe';

export const CODE_LENGTH = 6;

/** The message key for a failed code step, or `null`. */
export function codeFailureKey(error: unknown): string | null {
  if (!error) return null;
  const slug = error instanceof ApiFailure ? error.slug : 'unexpected';
  if (slug === 'second-factor-invalid' || slug === 'validation-failed') return 'signIn.codeInvalid';
  if (slug === 'rate-limited') return 'common.rateLimited';
  if (slug === 'session-expired' || slug === 'unauthenticated') return 'signIn.sessionExpired';
  if (slug === 'network') return 'common.errorNetwork';
  return 'common.errorUnexpected';
}

/**
 * One 6-digit code sent to `send` (verify or confirm): a wrong code clears the
 * field for the next one; success refreshes `me`, which then says `verified`.
 */
export function useCodeStep(send: (code: string) => Promise<unknown>, onVerified: () => void) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const mutation = useMutation({
    mutationFn: (value: string) => send(value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      onVerified();
    },
    onError: () => setCode(''),
  });
  const submit = (value = code) => {
    if (value.length !== CODE_LENGTH) return setIncomplete(true);
    setIncomplete(false);
    mutation.mutate(value);
  };
  const failure = mutation.isPending ? null : codeFailureKey(mutation.error);
  const expired = mutation.error instanceof ApiFailure && (mutation.error.slug === 'session-expired' || mutation.error.slug === 'unauthenticated');
  return { code, setCode, submit, pending: mutation.isPending, incomplete, failure, expired };
}
